import { ru } from '@/i18n/locales/ru';
import { useState, useEffect, type CSSProperties, type FormEvent } from 'react';
import { portalApi } from '../../portal/api';
import type {
  ColabRunStatsOut,
  ContentOverviewOut,
  ModerationTaskOut,
  PublicContentListItem,
  PublicPageListItem,
  SocialHandoffOut,
  SourceRegistryOut,
} from '../../portal/types';

const TABS = [
  { id: 'OVERVIEW' as const, label: 'Обзор' },
  { id: 'PAGES' as const, label: 'Публичные страницы' },
  { id: 'PUBLICATIONS' as const, label: 'Публикации' },
  { id: 'CATALOG' as const, label: 'Публичный каталог' },
  { id: 'MODERATION' as const, label: 'Очередь модерации' },
  { id: 'SOURCES' as const, label: 'Официальные источники' },
  { id: 'COLAB' as const, label: 'Автосбор источников' },
  { id: 'SMM' as const, label: 'Соцсети / ЕНУ' },
];

const CATALOG_KINDS = ['COMPETITION', 'MATERIAL', 'EDUCATION', 'VACANCY'] as const;
const STATUS_OPTIONS = ['', 'DRAFT', 'SUBMITTED_FOR_MODERATION', 'PUBLISHED', 'AUTO_PUBLISHED', 'ARCHIVED'];

function contentLabel(category: 'status' | 'kind' | 'socialStatus', value: string): string {
  const key = category === 'socialStatus' ? `portal.social.status.${value}` : `publisher.${category}.${value}`;
  return Object.hasOwn(ru, key) ? ru[key as keyof typeof ru] : category === 'kind' ? 'Материал' : 'Статус уточняется';
}

/** Public content center — owner-uat-* shell inside owner overlay. */
export const OwnerContentCenter = () => {
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]['id']>('OVERVIEW');

  return (
    <div>
      <section className="owner-uat-route-hero" style={{ '--owner-uat-accent': '#e2ad3a' } as CSSProperties}>
        <div>
          <div className="owner-uat-route-kicker">Публичная часть</div>
          <h2>Публичная часть и контент</h2>
          <p>Обзор, страницы, публикации, модерация, источники, автосбор и соцсети.</p>
        </div>
      </section>

      <div className="owner-uat-switch" style={{ margin: '12px 0', flexWrap: 'wrap' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`owner-uat-switch-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'OVERVIEW' && <OverviewTab />}
      {activeTab === 'PAGES' && <PagesTab />}
      {activeTab === 'PUBLICATIONS' && <PublicationsTab />}
      {activeTab === 'CATALOG' && <CatalogTab />}
      {activeTab === 'MODERATION' && <ModerationTab />}
      {activeTab === 'SOURCES' && <SourcesTab />}
      {activeTab === 'COLAB' && <ColabTab />}
      {activeTab === 'SMM' && <SmmTab />}
    </div>
  );
};

const OverviewTab = () => {
  const [overview, setOverview] = useState<ContentOverviewOut | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.contentOverview().then(setOverview).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;
  if (!overview) return <div className="owner-uat-empty">Нет данных</div>;

  const colabStatus =
    overview.last_colab_status === 'COMPLETED'
      ? 'Успешно'
      : overview.last_colab_status === 'NEVER_RUN'
        ? 'Ещё не запускался'
        : overview.last_colab_status || '—';

  return (
    <div className="owner-uat-kpis">
      <article className="owner-uat-kpi" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
        <small>Ожидают модерации</small>
        <strong>{overview.pending_moderation}</strong>
      </article>
      <article className="owner-uat-kpi" style={{ '--kpi': '#49ddb7' } as CSSProperties}>
        <small>Опубликовано</small>
        <strong>{overview.published_count}</strong>
      </article>
      <article className="owner-uat-kpi" style={{ '--kpi': '#41d2ff' } as CSSProperties}>
        <small>Источники (активные)</small>
        <strong>
          {overview.active_sources}/{overview.sources_count}
        </strong>
      </article>
      <article className="owner-uat-kpi" style={{ '--kpi': '#9e80ff' } as CSSProperties}>
        <small>Последний автосбор</small>
        <strong style={{ fontSize: 16 }}>{colabStatus}</strong>
        <span style={{ fontSize: 11, color: '#8faebb' }}>
          {overview.last_colab_run_at
            ? new Date(overview.last_colab_run_at).toLocaleString('ru-RU')
            : '—'}
          {overview.last_colab_published > 0 ? ` · +${overview.last_colab_published}` : ''}
        </span>
      </article>
    </div>
  );
};

const PagesTab = () => {
  const [pages, setPages] = useState<PublicPageListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PublicPageListItem | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    portalApi.listPages().then(setPages).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (page: PublicPageListItem) => {
    setEditing(page);
    setTitle(page.title);
    setBody(page.body);
  };

  const saveDraft = async () => {
    if (!editing) return;
    setSaving(true);
    await portalApi.savePageDraft(editing.slug, title, body, editing.page_type);
    setSaving(false);
    setEditing(null);
    load();
  };

  const publish = async (slug: string) => {
    if (!confirm('Опубликовать страницу?')) return;
    await portalApi.publishPage(slug);
    load();
  };

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;

  return (
    <section className="owner-uat-panel">
      <header>
        <div>
          <h3>Публичные страницы</h3>
          <p>О нас, инструкция и контакты</p>
        </div>
      </header>
      <div className="owner-uat-panel-body" style={{ display: 'grid', gap: 10 }}>
        {pages.map((page) => (
          <article
            key={page.slug}
            className="owner-uat-card"
            style={{ '--owner-uat-accent': '#e2ad3a' } as CSSProperties}
          >
            <div className="owner-uat-actions" style={{ marginBottom: 6 }}>
              <strong>{page.title}</strong>
              <span className="owner-uat-chip" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                {contentLabel('status', page.status)}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#8faebb' }}>/{page.slug}</p>
            {page.body ? (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#c5d6de' }}>
                {page.body.slice(0, 140)}
                {page.body.length > 140 ? '…' : ''}
              </p>
            ) : null}
            <div className="owner-uat-actions" style={{ marginTop: 10 }}>
              <button type="button" className="owner-uat-btn" onClick={() => openEdit(page)}>
                Редактировать
              </button>
              <button type="button" className="owner-uat-btn primary" onClick={() => void publish(page.slug)}>
                Опубликовать
              </button>
            </div>
          </article>
        ))}
      </div>

      {editing ? (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            border: '1px solid rgba(65,210,255,.25)',
            borderRadius: 12,
            background: 'rgba(8,20,32,.6)',
          }}
        >
          <h4 style={{ margin: '0 0 12px' }}>Редактирование: {editing.slug}</h4>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Заголовок</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Текст</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <div className="owner-uat-actions">
            <button type="button" className="owner-uat-btn primary" disabled={saving} onClick={() => void saveDraft()}>
              {saving ? 'Сохранение…' : 'Сохранить черновик'}
            </button>
            <button type="button" className="owner-uat-btn" onClick={() => setEditing(null)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const PublicationsTab = () => {
  const [items, setItems] = useState<PublicContentListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    portalApi
      .manageContent({ status_filter: statusFilter || undefined, page: 1 })
      .then((res) => setItems(res.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  return (
    <section className="owner-uat-panel">
      <header>
        <div>
          <h3>Публикации</h3>
          <p>Все материалы с фильтром по статусу</p>
        </div>
        <select
          aria-label="Статус публикации"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
        >
          <option value="">Все статусы</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {contentLabel('status', s)}
            </option>
          ))}
        </select>
      </header>
      <div className="owner-uat-panel-body" style={{ display: 'grid', gap: 10 }}>
        {loading ? (
          <div className="owner-uat-empty">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="owner-uat-empty">Публикаций нет</div>
        ) : (
          items.map((item) => (
            <article
              key={item.id}
              className="owner-uat-card"
              style={{ '--owner-uat-accent': '#49ddb7' } as CSSProperties}
            >
              <div className="owner-uat-actions" style={{ marginBottom: 6 }}>
                <span className="owner-uat-chip" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
                  {contentLabel('kind', item.kind)}
                </span>
                <span className="owner-uat-chip" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                  {contentLabel('status', item.status)}
                </span>
              </div>
              <strong>{item.title}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8faebb' }}>
                {item.author_name || '—'} · {new Date(item.created_at).toLocaleString('ru-RU')}
              </p>
            </article>
          ))
        )}
      </div>
    </section>
  );
};

const CatalogTab = () => {
  const [kind, setKind] = useState<(typeof CATALOG_KINDS)[number]>('COMPETITION');
  const [items, setItems] = useState<PublicContentListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    portalApi.list({ kind, page: 1 }).then((res) => setItems(res.items)).finally(() => setLoading(false));
  }, [kind]);

  return (
    <section className="owner-uat-panel">
      <header>
        <div>
          <h3>Публичный каталог</h3>
          <p>Опубликованные объекты по типу</p>
        </div>
        <div className="owner-uat-switch">
          {CATALOG_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`owner-uat-switch-btn${kind === k ? ' active' : ''}`}
              onClick={() => setKind(k)}
            >
              {k}
            </button>
          ))}
        </div>
      </header>
      <div className="owner-uat-panel-body" style={{ display: 'grid', gap: 10 }}>
        {loading ? (
          <div className="owner-uat-empty">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="owner-uat-empty">Записей нет</div>
        ) : (
          items.map((item) => (
            <article
              key={item.id}
              className="owner-uat-card"
              style={{ '--owner-uat-accent': '#41d2ff' } as CSSProperties}
            >
              <strong>{item.title}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8faebb' }}>{item.summary.slice(0, 160)}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
};

const ModerationTab = () => {
  const [tasks, setTasks] = useState<ModerationTaskOut[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    portalApi
      .getModerationQueue()
      .then((res) => setTasks(res.items))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const decide = async (id: string, decision: string, requireComment = false) => {
    let comment = '';
    if (requireComment) {
      comment = window.prompt('Комментарий для автора:') ?? '';
      if (!comment.trim()) return;
    }
    await portalApi.moderationDecision(id, decision, comment.trim());
    load();
  };

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;
  if (tasks.length === 0) return <div className="owner-uat-empty">Очередь пуста</div>;

  return (
    <div className="owner-uat-grid2">
      {tasks.map((task) => (
        <article
          key={task.id}
          className="owner-uat-card"
          style={{ '--owner-uat-accent': '#e2ad3a' } as CSSProperties}
        >
          <div className="owner-uat-actions" style={{ marginBottom: 6 }}>
            <span className="owner-uat-chip" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              {task.content_kind}
            </span>
            <span style={{ fontSize: 12, color: '#8faebb' }}>
              {new Date(task.created_at).toLocaleString('ru-RU')}
            </span>
          </div>
          <strong>{task.content_title}</strong>
          <p style={{ marginTop: 8, fontSize: 12, color: '#8faebb' }}>
            Автор: {task.author_role}
          </p>
          <div className="owner-uat-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="owner-uat-btn primary"
              onClick={() => void decide(task.id, 'APPROVE')}
            >
              Одобрить
            </button>
            <button
              type="button"
              className="owner-uat-btn"
              onClick={() => void decide(task.id, 'CHANGES_REQUESTED', true)}
            >
              Вернуть на доработку
            </button>
            <button
              type="button"
              className="owner-uat-btn danger"
              onClick={() => void decide(task.id, 'REJECT', true)}
            >
              Отклонить
            </button>
          </div>
        </article>
      ))}
    </div>
  );
};

const SourcesTab = () => {
  const [sources, setSources] = useState<SourceRegistryOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', base_domain: '', kinds: 'NEWS', allowed_paths: '' });

  const load = () => {
    setLoading(true);
    portalApi.listSources().then(setSources).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const toggle = async (id: string, current: string) => {
    const next = current === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    await portalApi.updateSourceStatus(id, next);
    load();
  };

  const test = async (id: string) => {
    const res = await portalApi.testSource(id);
    alert(res.success ? 'Успешно' : `Ошибка: ${res.error}`);
  };

  const createSource = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.base_domain.trim()) return;
    await portalApi.createSource({
      name: form.name.trim(),
      base_domain: form.base_domain.trim(),
      kinds: form.kinds.trim(),
      allowed_paths: form.allowed_paths.trim() || undefined,
    });
    setForm({ name: '', base_domain: '', kinds: 'NEWS', allowed_paths: '' });
    setShowForm(false);
    load();
  };

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;

  return (
    <section className="owner-uat-panel">
      <header>
        <div>
          <h3>Официальные источники</h3>
          <p>Домены, статусы и проверка сбора</p>
        </div>
        <button type="button" className="owner-uat-btn primary" onClick={() => setShowForm((v) => !v)}>
          + Добавить источник
        </button>
      </header>

      {showForm ? (
        <form
          onSubmit={(e) => void createSource(e)}
          style={{
            marginBottom: 12,
            padding: 14,
            borderRadius: 12,
            border: '1px solid rgba(73,221,183,.25)',
            background: 'rgba(8,20,32,.6)',
            display: 'grid',
            gap: 8,
          }}
        >
          <input
            placeholder="Название"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            style={{ padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <input
            placeholder="Домен (example.kz)"
            value={form.base_domain}
            onChange={(e) => setForm((f) => ({ ...f, base_domain: e.target.value }))}
            required
            style={{ padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <input
            placeholder="Типы (NEWS,COMPETITION)"
            value={form.kinds}
            onChange={(e) => setForm((f) => ({ ...f, kinds: e.target.value }))}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <input
            placeholder="Путь (необязательно, /news)"
            value={form.allowed_paths}
            onChange={(e) => setForm((f) => ({ ...f, allowed_paths: e.target.value }))}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #1e293b', background: '#0c1322', color: '#e2e8f0' }}
          />
          <div className="owner-uat-actions">
            <button type="submit" className="owner-uat-btn primary">
              Создать
            </button>
            <button type="button" className="owner-uat-btn" onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </form>
      ) : null}

      <div className="owner-uat-panel-body" style={{ display: 'grid', gap: 10 }}>
        {sources.map((source) => (
          <article
            key={source.id}
            className="owner-uat-card"
            style={{ '--owner-uat-accent': '#41d2ff' } as CSSProperties}
          >
            <div className="owner-uat-actions" style={{ marginBottom: 6 }}>
              <strong>{source.name}</strong>
              <span
                className="owner-uat-chip"
                style={
                  {
                    '--badge': source.status === 'ACTIVE' ? '#49ddb7' : '#ff7b65',
                  } as CSSProperties
                }
              >
                {source.status === 'ACTIVE' ? 'Активен' : 'На паузе'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#8faebb' }}>
              {source.base_domain} · {source.kinds}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8faebb' }}>
              Последний сбор:{' '}
              {source.last_success_at
                ? new Date(source.last_success_at).toLocaleString('ru-RU')
                : 'ещё не было'}
            </p>
            <div className="owner-uat-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="owner-uat-btn"
                onClick={() => void toggle(source.id, source.status)}
              >
                {source.status === 'ACTIVE' ? 'Пауза' : 'Активировать'}
              </button>
              <button
                type="button"
                className="owner-uat-btn"
                onClick={() => void test(source.id)}
              >
                Проверить
              </button>
            </div>
          </article>
        ))}
        {!sources.length ? <div className="owner-uat-empty">Источников нет</div> : null}
      </div>
    </section>
  );
};

const ColabTab = () => {
  const [stats, setStats] = useState<ColabRunStatsOut | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    portalApi.colabStats().then(setStats).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const runNow = async () => {
    if (!confirm('Запустить автосбор сейчас?')) return;
    setLoading(true);
    await portalApi.colabRunNow();
    load();
  };

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;

  const statusRu =
    stats?.status === 'SUCCESS'
      ? 'Успешно'
      : stats?.status === 'COMPLETED'
        ? 'Завершён'
        : stats?.status
          ? 'С ошибками'
          : 'Нет данных';

  return (
    <div>
      <section className="owner-uat-route-hero" style={{ '--owner-uat-accent': '#9e80ff' } as CSSProperties}>
        <div>
          <div className="owner-uat-route-kicker">Автосбор</div>
          <h2>Статистика последнего запуска</h2>
          <p>Расписание: 04:00 · Алматы</p>
        </div>
        <button type="button" className="owner-uat-btn primary" onClick={() => void runNow()}>
          Запустить сейчас
        </button>
      </section>
      <div className="owner-uat-kpis" style={{ marginTop: 12 }}>
        <article className="owner-uat-kpi" style={{ '--kpi': '#9e80ff' } as CSSProperties}>
          <small>Статус</small>
          <strong style={{ fontSize: 18 }}>{statusRu}</strong>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#41d2ff' } as CSSProperties}>
          <small>Найдено</small>
          <strong>{stats?.items_found || 0}</strong>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#49ddb7' } as CSSProperties}>
          <small>Опубликовано</small>
          <strong>{stats?.items_published || 0}</strong>
        </article>
        <article className="owner-uat-kpi" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
          <small>Время</small>
          <strong>{stats?.duration_seconds || 0} с</strong>
        </article>
      </div>
      {stats?.recent_errors?.length ? (
        <div className="owner-uat-empty" style={{ marginTop: 12, borderColor: 'rgba(255,123,101,.35)' }}>
          <strong>Ошибки последнего запуска</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {stats.recent_errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const SmmTab = () => {
  const [items, setItems] = useState<SocialHandoffOut[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.listHandoffs().then(setItems).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="owner-uat-empty">Загрузка…</div>;
  if (items.length === 0) return <div className="owner-uat-empty">Передач нет</div>;

  return (
    <div className="owner-uat-grid2">
      {items.map((item) => (
        <article
          key={item.id}
          className="owner-uat-card"
          style={{ '--owner-uat-accent': '#49ddb7' } as CSSProperties}
        >
          <div className="owner-uat-actions" style={{ marginBottom: 6 }}>
            <span className="owner-uat-chip" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              {item.material_type}
            </span>
            <span className="owner-uat-chip" style={{ '--badge': '#41d2ff' } as CSSProperties}>
              {contentLabel('socialStatus', item.status)}
            </span>
          </div>
          <strong>{item.title}</strong>
          <p style={{ marginTop: 8 }}>{item.description}</p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#8faebb' }}>
            Каналы: {item.channels} · {item.author_name || 'автор'} ·{' '}
            {new Date(item.created_at).toLocaleString('ru-RU')}
          </p>
        </article>
      ))}
    </div>
  );
};
